import { config } from '../config';
import type {
  SetPieceFlags,
  SetPieceMarker,
  SetPieceMarkerType,
  SetPieceScheme,
  SetPieceType,
} from '../types';

/**
 * SP-2 … SP-6 — **the set-piece rules, as pure functions** (build unit U-12).
 *
 * Everything a set-piece capture *decides* lives here: which marker a tap produces, what a repeat tap
 * on that marker turns it into, and how a pointer position on a rendered diagram becomes the
 * normalized geometry SP-5 requires. None of it touches the DOM, a clock or a database, so every rule
 * below is driven by a Node suite rather than inferred from a screenshot.
 *
 * ── SP-5 is the reason the geometry is here and not in the component ─────────
 *
 * > *"Marker geometry stored normalized as 0–1 box coordinates, for resolution-independent re-render
 * > on the share card."*
 *
 * A marker is **not** a pixel offset. A pixel offset is a fact about one viewport in one orientation
 * on one device, and CARD-1 re-renders these markers onto an offscreen canvas of an entirely
 * different size. {@link normalizeToBox} converts once, at the moment of the tap, from the box the
 * user actually touched — so a rotation, a re-render, a different phone and the share card all read
 * the same marker.
 *
 * Two properties make that true and both are asserted:
 *
 * - **Clamped**, because a fingertip can land a pixel outside the diagram's bounds and a marker at
 *   `-0.0003` renders off the card rather than on its edge.
 * - **Rounded to `GEOMETRY_DECIMALS`** here as well as at the repo boundary. The repo rounds so
 *   INV-12's byte-determinism is reachable; rounding here too means the value the surface renders
 *   from is byte-identical to the value it stored, so a marker cannot shift by a sub-pixel on the
 *   next read. Rounding twice is idempotent, and the two call sites protect different things.
 *
 * **A degenerate box yields `null`, never `NaN`.** An element measured before layout reports
 * `width: 0`, and dividing by it produces `Infinity`/`NaN` — which would travel all the way to a
 * stored row and only surface as a marker that will not render. The refusal is the mechanism.
 *
 * ── The marker cycle is data, and B-23 collected on that ────────────────────
 *
 * SP-2 and SP-3 gave the two corner types different marker vocabularies. Expressing that as a
 * per-type table rather than as branches was a bet that a later kind would be a **row here**, not a
 * new `if` in a component — the same argument §3.5 makes for storing `markers` as JSON instead of
 * rows. B-23 added free kicks and the bet paid: two rows in {@link MARKER_CYCLE}, one entry in
 * {@link EXCLUSIVE_MARKER_TYPES}, and no branch anywhere in this file names a set-piece kind.
 *
 * What was *not* true is the claim, made in a comment here and in `types/index.ts` until B-23, that
 * the `SetPieceType` union "stays open". It is a closed union that several exhaustive `Record`s and
 * a `z.enum` enumerate; adding a value is a compile error in each of them. That is a good property —
 * the compile errors are the checklist of everything a new kind must answer for — but it is the
 * opposite of open, and a comment saying otherwise is how the next reader gets misled.
 */

/**
 * The set-piece kinds that ship.
 *
 * SP-6 said *"v1 UI ships corners only"*; requirements §3.2.14 asked for *"a dedicated mode for
 * **corners/free kicks**"*, and the milestone review found the narrowing to corners had been adopted
 * silently. B-23 built the free kicks. **Touchline B's sheet renders all four from this list;
 * A's sheet renders corners only, from its own explicit list** — A is frozen insurance and does not
 * gain features (`ui/capture/SetPieceScreen.tsx` says so at the render site).
 *
 * `countByType` reports a count per entry here, so a kind added to this list is a category in SP-4's
 * counts without any surface being told about it.
 */
export const SET_PIECE_TYPES = [
  'defCorner',
  'attCorner',
  'defFreeKick',
  'attFreeKick',
] as const satisfies readonly SetPieceType[];

/**
 * How every surface names a set-piece kind — **one map, because there is one vocabulary**.
 *
 * Until B-23 these four strings lived in three places (A's sheet, B's match record, and a ternary in
 * `domain/card.ts`), which was survivable while there were two of them and stopped being so at four:
 * the ternary would have labelled every free kick "Attacking corner" on a delivered PNG with nothing
 * to typecheck. A share card, a match record and a capture sheet disagreeing about what a capture is
 * called is a defect the compiler cannot see, so the map is here — in the pure module both apps
 * already import — and each surface renders it rather than restating it.
 *
 * B's picker splits these into two lines for its 2×2 grid; that split is B's layout, derived from
 * these words and pinned to them by a test rather than typed out again.
 */
export const SET_PIECE_LABEL: Readonly<Record<SetPieceType, string>> = {
  defCorner: 'Defensive corner',
  attCorner: 'Attacking corner',
  defFreeKick: 'Defensive free kick',
  attFreeKick: 'Attacking free kick',
};

/** SP-2's segmented control. */
export const SET_PIECE_SCHEMES = ['zonal', 'man', 'hybrid'] as const satisfies readonly SetPieceScheme[];

/** SP-3's swing. */
export const SET_PIECE_SWINGS = ['in', 'out'] as const;

/** SP-3's target zone. */
export const SET_PIECE_TARGET_ZONES = ['near', 'far', 'edge'] as const;

/**
 * SP-2's *"repeat taps cycle marker type"* and SP-3's *"delivery target and optional screener"*, as
 * one table.
 *
 * The **first entry is what a tap on empty diagram space produces**, and the order is the cycle a
 * repeat tap walks. `defCorner` is SP-2's four values verbatim. `attCorner` is SP-3's two: the first
 * tap marks the delivery target, and every later one is a screener — see {@link nextMarkerType}.
 *
 * ── Why both free-kick cycles start at `kickPoint` (B-23) ───────────────────
 *
 * A corner is taken from a corner arc: the restart point is a property of the *kind*, so no marker
 * has to say where it is. A free kick is not — a free kick 22 yards out central and one on the
 * touchline by the corner flag are different events, and the diagram is the only place that fact can
 * live. So the **first tap on a free kick marks where the ball is**, and `kickPoint` is exclusive
 * ({@link EXCLUSIVE_MARKER_TYPES}) because there is one ball.
 *
 * After that the two free kicks diverge exactly as the corners do: defending, you record the wall
 * and how the rest are marking; attacking, you record where the delivery is aimed and who screens.
 * The wall is **markers, not a scheme** — `SET_PIECE_SCHEMES` is untouched, so nothing about free
 * kicks reaches the segmented control A also renders.
 */
export const MARKER_CYCLE: Readonly<Record<SetPieceType, MarkerCycle>> = {
  defCorner: ['zonal', 'man', 'nearPost', 'edgeOfBox'],
  attCorner: ['target', 'screener'],
  defFreeKick: ['kickPoint', 'wall', 'zonal', 'man'],
  attFreeKick: ['kickPoint', 'target', 'screener'],
};

/**
 * A **non-empty** tuple, so `cycle[0]` is a marker type rather than `SetPieceMarkerType | undefined`.
 *
 * The alternative under `noUncheckedIndexedAccess` is a fallback at every read of the head, and a
 * fallback is a branch nothing can ever exercise — an untestable line that hides the real invariant.
 * A type that cannot express an empty cycle is the invariant, and adding an empty row to the table
 * above stops compiling.
 */
type MarkerCycle = readonly [SetPieceMarkerType, ...SetPieceMarkerType[]];

/**
 * Marker types of which a capture may hold **at most one**.
 *
 * SP-3 says *"tap marks **the** delivery target and **an optional** screener"* — one target, any
 * number of screeners. Without this the cycle would happily produce two delivery targets and CARD-1's
 * thumbnail would show a corner aimed at two places. `kickPoint` (B-23) is exclusive for the blunter
 * reason that there is one ball. Assigning an exclusive type **demotes** the previous holder rather
 * than deleting it, because the user placed that marker deliberately and silently removing it is a
 * worse answer than reclassifying it.
 *
 * This list is the whole mechanism: {@link nextMarkerType} will not hand a new marker a type already
 * taken, and {@link demoteOthers} strips the type off every other marker when a repeat tap assigns
 * it. Neither reads a set-piece kind, so adding an exclusive type to a new cycle needs no other edit.
 */
const EXCLUSIVE_MARKER_TYPES: readonly SetPieceMarkerType[] = ['target', 'kickPoint'];

/** A point inside the 0–1 box. */
export interface BoxPoint {
  readonly x: number;
  readonly y: number;
}

/** The rendered diagram's box, as `getBoundingClientRect()` reports it. */
export interface DiagramBox {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

/**
 * SP-5 — a pointer position on the rendered diagram, as normalized 0–1 box coordinates.
 *
 * @returns `null` when the box is degenerate or either input is not finite. See the module note: a
 *   `NaN` marker is a marker that stores successfully and never renders, which is the failure mode
 *   worth refusing rather than propagating.
 */
export function normalizeToBox(box: DiagramBox, clientX: number, clientY: number): BoxPoint | null {
  if (!Number.isFinite(clientX) || !Number.isFinite(clientY)) return null;
  if (!Number.isFinite(box.left) || !Number.isFinite(box.top)) return null;
  if (!Number.isFinite(box.width) || !Number.isFinite(box.height)) return null;
  if (box.width <= 0 || box.height <= 0) return null;
  return {
    x: round01((clientX - box.left) / box.width),
    y: round01((clientY - box.top) / box.height),
  };
}

/** Clamp to the 0–1 box, then round to `GEOMETRY_DECIMALS` (NFR-9 supplies the number). */
function round01(value: number): number {
  const factor = 10 ** config.GEOMETRY_DECIMALS;
  return Math.round(Math.min(1, Math.max(0, value)) * factor) / factor;
}

/**
 * SP-2 / SP-3 — the type a **new** marker takes on this capture.
 *
 * Walk the cycle and take the first type this capture may still add: a non-exclusive type is always
 * available, an exclusive one only while no marker holds it. So `defCorner` always starts at the head
 * (`zonal` is not exclusive); `attCorner` marks the delivery target first and every later tap a
 * screener; a free kick marks the ball first, then walls or screeners — SP-3's sentence and B-23's,
 * without the user having to choose a mode first (LIVE-9: taps alone).
 *
 * B-23 generalised this from a head-only check to the walk, because `attFreeKick` has **two**
 * exclusive types in one cycle (`kickPoint` then `target`) and the head-only form would have handed
 * the second tap a duplicate `target` forever. The two corner cycles are unaffected: `defCorner`
 * returns at the first candidate, and `attCorner`'s walk reproduces `taken ? cycle[1] : head` exactly.
 *
 * The final `cycle[0]` is reached only by a cycle whose every member is exclusive and taken — no
 * shipping cycle is that shape, and returning the head is the same answer a duplicate tap on a
 * one-value cycle would get.
 */
export function nextMarkerType(
  type: SetPieceType,
  markers: readonly SetPieceMarker[],
): SetPieceMarkerType {
  const cycle = MARKER_CYCLE[type];
  for (const candidate of cycle) {
    if (!EXCLUSIVE_MARKER_TYPES.includes(candidate)) return candidate;
    if (!markers.some((marker) => marker.type === candidate)) return candidate;
  }
  return cycle[0];
}

/**
 * SP-2 / SP-3 — *"tap drops a marker"*.
 *
 * Appends rather than inserting, so an existing marker's index — which is the identity the surface
 * cycles by — never moves under a tap that was aimed at a different marker.
 */
export function placeMarker(
  type: SetPieceType,
  markers: readonly SetPieceMarker[],
  point: BoxPoint,
): SetPieceMarker[] {
  return [...markers, { x: point.x, y: point.y, type: nextMarkerType(type, markers) }];
}

/**
 * SP-2 — *"repeat taps on a marker cycle its type"*, through {@link MARKER_CYCLE} for this type.
 *
 * The **position never moves**: a repeat tap re-classifies the marker the user already placed, so a
 * tap landing 3 pt off its centre must not drag it. An out-of-range index returns the list unchanged
 * rather than throwing — the caller resolves the index out of the DOM, and a stale index is a
 * no-op, not a crash on a live capture surface.
 */
export function cycleMarkerAt(
  type: SetPieceType,
  markers: readonly SetPieceMarker[],
  index: number,
): SetPieceMarker[] {
  if (!Number.isInteger(index) || index < 0) return [...markers];
  const current = markers[index];
  if (current === undefined) return [...markers];

  const cycle = MARKER_CYCLE[type];
  const at = cycle.indexOf(current.type);
  // A marker whose type is not in this capture's cycle (a type change, or an imported archive from a
  // later version — SP-6) re-enters at the head rather than being left un-cyclable.
  const nextType = cycle[(at + 1) % cycle.length] ?? cycle[0];

  const next = markers.map((marker, position) =>
    position === index ? { ...marker, type: nextType } : marker,
  );
  return EXCLUSIVE_MARKER_TYPES.includes(nextType) ? demoteOthers(cycle, next, index, nextType) : next;
}

/**
 * See {@link EXCLUSIVE_MARKER_TYPES} — the previous holder is reclassified, never deleted.
 *
 * The demotion target is the first **non-exclusive** type in the cycle, not merely the first type
 * that differs from the one being assigned. B-23 corrected that, and the case is worth stating
 * because it is not the obvious one: `attFreeKick`'s cycle is `['kickPoint', 'target', 'screener']`,
 * so a repeat tap that walks a screener round to `kickPoint` displaces whichever marker held the
 * ball — and "the first type that isn't `kickPoint`" is `target`. On a capture that already had a
 * delivery target, the demotion would have minted a **second** one: the exact duplication this
 * function exists to prevent, reintroduced through the other exclusive type. A displaced marker
 * becomes a `screener` — kept, positioned, and holding no special role, which is the only honest
 * thing to say about a mark whose role the user just gave to something else.
 *
 * Both corner cycles demote to the same value under either rule (`attCorner` → `screener`;
 * `defCorner` has no exclusive type, so this is never called for it).
 *
 * The `?? exclusiveType` arm is for a cycle of nothing but exclusive types, which no shipping cycle
 * is; it leaves the marker where it was rather than inventing a type that is not in the vocabulary.
 */
function demoteOthers(
  cycle: MarkerCycle,
  markers: readonly SetPieceMarker[],
  keepIndex: number,
  exclusiveType: SetPieceMarkerType,
): SetPieceMarker[] {
  const demoted = cycle.find((candidate) => !EXCLUSIVE_MARKER_TYPES.includes(candidate)) ?? exclusiveType;
  return markers.map((marker, position) =>
    position !== keepIndex && marker.type === exclusiveType ? { ...marker, type: demoted } : marker,
  );
}

/**
 * SP-2's two toggles, as one function over `flags`.
 *
 * `undefined` and `false` are the same state to the user and are stored as they arrive; the toggle
 * reads the current value as "on iff `=== true`", so an absent key toggles on rather than becoming
 * `false` and requiring a second tap to do anything visible.
 */
export function toggleFlag(flags: SetPieceFlags, key: 'leftUpTop' | 'edgeMarked'): SetPieceFlags {
  return { ...flags, [key]: flags[key] !== true };
}

/**
 * SP-4 — *"the UI shows captures-per-category count for the match"*.
 *
 * Every shipping category is present with a `0` rather than only the ones that have captures, so the
 * sheet renders a stable set of controls whose labels change and whose positions do not.
 */
export function countByType(
  rows: readonly { readonly type: SetPieceType }[],
): Record<SetPieceType, number> {
  const counts = {} as Record<SetPieceType, number>;
  for (const type of SET_PIECE_TYPES) counts[type] = 0;
  for (const row of rows) counts[row.type] = (counts[row.type] ?? 0) + 1;
  return counts;
}

/**
 * Whether every marker on a capture is storable geometry.
 *
 * The command handler asks this before emitting a write. {@link normalizeToBox} already refuses to
 * produce a non-finite coordinate, so a violation means something reached the row without going
 * through it — which is precisely the path a guard on the *data* catches and a guard on the *caller*
 * does not.
 */
export function markersAreFinite(markers: readonly SetPieceMarker[]): boolean {
  return markers.every((marker) => Number.isFinite(marker.x) && Number.isFinite(marker.y));
}
